import { useEffect, useRef, useState } from "react";
import { Flex } from "antd";
import { colors } from "shared/theme/color";
import { translate } from "shared/languages";
import { LoyaltyMilestoneInfo, StampDistributionBucket } from "shared/types";
import { rewardLabel } from "shared/utils/loyalty";
import {
  VBOX_W,
  VBOX_H,
  PAD_LEFT,
  PAD_RIGHT,
  PAD_TOP,
  PLOT_H,
  countTicks,
  bandWidth,
  bandXFor,
  yFor,
} from "./chartUtils";
import { textPx } from "shared/constants/zoom";

// This chart sizes its viewBox to the container instead of scaling a fixed
// 520-unit box up to fit. The fixed box is uniformly scaled by
// preserveAspectRatio="meet", so it is HEIGHT-capped: widening the container
// only added letterbox (measured 608px of drawing in a 1406px card), and
// filling that width by scaling would have meant a 541px-tall chart for a
// max value of 1, with 26px axis labels. Matching viewBox units to CSS px
// keeps every font size and stroke exactly as authored and stretches only
// the thing that should stretch, the bands.
const MIN_VBOX_W = VBOX_W;

interface StampDistributionChartProps {
  buckets: StampDistributionBucket[];
  milestones: LoyaltyMilestoneInfo[];
  // Rendered height, which also sets the figure's scale (height / VBOX_H).
  // Width now follows the container, so it needs no headroom reserved for it.
  height?: number;
}

const TIP_H = 48;
// Third line (the reward) only appears over a milestone column.
const TIP_H_REWARD = 66;
// Rough px-per-char at a given font size (SVG has no text measurement without
// a DOM round-trip), so the box fits its content instead of clipping a long
// reward name. Same approach as RevenueCompositionChart.
const estimateTextWidth = (text: string, fontSize: number) => text.length * fontSize * 0.62;

const Legend = ({ color, label }: { color: string; label: string }) => (
  <Flex align="center" gap={6}>
    <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
    <span style={{ fontSize: textPx(12), color: colors.ink2 }}>{label}</span>
  </Flex>
);

// x = how many stamps a client holds, y = how many clients hold that many.
//
// Navy, with amber on the columns that pay out a reward. A ramp across the
// stamp axis was tried (darker = closer to the reward) and dropped: the light
// end of any ramp wide enough to read as a ramp is too faint against the
// card, and the x-axis is already ordered, so the colour encoded something
// the axis says anyway. Milestone tinting is the opposite case — which stamp
// numbers pay out is NOT in the axis, and it was previously carried only by a
// small dot under the baseline that is easy to miss.
//
// The dot stays as the backstop: a milestone with no clients has no bar to
// tint, so colour is the primary read and the dot covers the empty columns.
const StampDistributionChart = ({
  buckets,
  milestones,
  height = VBOX_H,
}: StampDistributionChartProps) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [vboxW, setVboxW] = useState(MIN_VBOX_W);

  // Width in viewBox units that renders flush to the container, given that
  // the figure is still drawn at height/VBOX_H (1.4x on this page). Solving
  // for it rather than measuring 1:1 keeps every authored font size and
  // stroke exactly as it renders today and removes the letterbox entirely;
  // only the bands stretch.
  //
  // offsetWidth (layout px) rather than a bounding rect (visual px): mixing
  // those two coordinate spaces under any CSS zoom is the drag-snap-line bug
  // all over again.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const scale = height / VBOX_H;
    const measure = () =>
      setVboxW(Math.max(MIN_VBOX_W, Math.round(el.offsetWidth / scale)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [height]);

  const isEmpty = buckets.every((b) => b.clients === 0);
  // Integer ticks: these are people, and a "0 / 1 / 1" axis is worse than no
  // axis at all (see countTicks).
  const { top: max, ticks } = countTicks(Math.max(...buckets.map((b) => b.clients), 1));
  const baselineY = PAD_TOP + PLOT_H;
  const n = buckets.length;
  const plotW = vboxW - PAD_LEFT - PAD_RIGHT;
  // Band scale, never xFor's point scale — a bar centred on an edge point
  // overflows the plot and paints over the y-axis labels (see chartUtils).
  const band = bandWidth(n, plotW);
  // Cap raised from 24 with the width: at a full-card width a 24px bar in a
  // 130px band reads as a hairline stranded in white space.
  const barWidth = Math.min(44, Math.max(8, band * 0.5));
  const milestoneAt = new Map(milestones.map((m) => [m.stamp_number, m]));

  const hovered = hoverIndex !== null ? buckets[hoverIndex] : null;
  const hoveredMilestone = hovered ? milestoneAt.get(hovered.stamps) : undefined;
  const hoveredReward = hoveredMilestone ? rewardLabel(hoveredMilestone) : null;
  const hoveredX = hoverIndex !== null ? bandXFor(hoverIndex, n, plotW) : 0;
  const tipH = hoveredReward ? TIP_H_REWARD : TIP_H;
  const tipW = hovered
    ? Math.max(
        132,
        Math.ceil(estimateTextWidth(hoveredReward ?? "", 12)) + 24,
      )
    : 0;
  const tipLeft = hovered
    ? Math.min(Math.max(hoveredX - tipW / 2, PAD_LEFT), vboxW - PAD_RIGHT - tipW)
    : 0;
  const tipTop = hovered
    ? Math.max(yFor(hovered.clients, max) - tipH - 10, PAD_TOP)
    : 0;

  return (
    <div ref={boxRef}>
      {isEmpty ? (
        <Flex
          align="center"
          justify="center"
          style={{ height, color: colors.ink3, fontSize: textPx(13) }}
        >
          {translate("analyticsPage.loyalty.distribution_empty")}
        </Flex>
      ) : (
        <>
          {/* Two colours means the "single series, no legend" rule no longer
              holds. Shown only when a milestone actually lands on the axis,
              so a key never explains a colour that isn't on screen. */}
          {buckets.some((b) => milestoneAt.has(b.stamps)) && (
            <Flex gap={20} style={{ marginBottom: 14 }}>
              <Legend
                color={colors.navy}
                label={translate("analyticsPage.loyalty.legend_collecting")}
              />
              <Legend
                color={colors.amber}
                label={translate("analyticsPage.loyalty.legend_milestone")}
              />
            </Flex>
          )}
          <svg
            width="100%"
            height={height}
            viewBox={`0 0 ${vboxW} ${VBOX_H}`}
            preserveAspectRatio="xMidYMid meet"
          >
            {ticks.map((v) => {
              const y = yFor(v, max);
              return (
                <g key={v}>
                  <line
                    x1={PAD_LEFT}
                    x2={vboxW - 8}
                    y1={y}
                    y2={y}
                    stroke={colors.line}
                    strokeWidth={1}
                  />
                  <text
                    x={PAD_LEFT - 8}
                    y={y + 3}
                    textAnchor="end"
                    fontSize={10}
                    fill={colors.ink3}
                  >
                    {v}
                  </text>
                </g>
              );
            })}

            {/* Milestone marker: a dot just BELOW the baseline, plus a bolded
                stamp number. It used to be a full-height hairline, which read
                as a thin bar standing at a stamp count where nobody actually
                is. An axis annotation must not be able to pass for data.
                Now redundant wherever the amber bar shows, and load-bearing
                where it doesn't: a milestone with no clients has no bar. */}
            {buckets.map((b, i) =>
              milestoneAt.has(b.stamps) ? (
                <circle
                  key={`m-${b.stamps}`}
                  cx={bandXFor(i, n, plotW)}
                  cy={baselineY + 6}
                  r={2.5}
                  fill={colors.amber}
                />
              ) : null,
            )}

            {buckets.map((b, i) => {
              const x = bandXFor(i, n, plotW);
              const topY = yFor(b.clients, max);
              const h = Math.max(0, baselineY - topY);
              const active = hoverIndex === i;
              const r = Math.min(4, barWidth / 2, h);
              const isMilestone = milestoneAt.has(b.stamps);
              return (
                <g key={b.stamps}>
                  {/* 4px rounded data-end, square at the baseline: a path
                      rather than rect+rx, which would round all four corners. */}
                  <path
                    d={`M ${x - barWidth / 2} ${baselineY}
                        L ${x - barWidth / 2} ${topY + r}
                        Q ${x - barWidth / 2} ${topY} ${x - barWidth / 2 + r} ${topY}
                        L ${x + barWidth / 2 - r} ${topY}
                        Q ${x + barWidth / 2} ${topY} ${x + barWidth / 2} ${topY + r}
                        L ${x + barWidth / 2} ${baselineY} Z`}
                    fill={isMilestone ? colors.amber : colors.navy}
                    opacity={hoverIndex === null || active ? 1 : 0.45}
                  />
                  <text
                    x={x}
                    y={VBOX_H - 8}
                    textAnchor="middle"
                    fontSize={10}
                    fill={isMilestone ? colors.amberDark : colors.ink3}
                    fontWeight={isMilestone ? 600 : 400}
                  >
                    {b.stamps}
                  </text>
                  {/* Full-band hit area, wider than the bar, so hovering near a
                      short bar doesn't need pixel-precise aim. */}
                  <rect
                    x={x - band / 2}
                    y={PAD_TOP}
                    width={band}
                    height={PLOT_H}
                    fill="transparent"
                    onMouseEnter={() => setHoverIndex(i)}
                    onMouseLeave={() => setHoverIndex(null)}
                  />
                </g>
              );
            })}

            {hovered && (
              <g pointerEvents="none">
                <rect
                  x={tipLeft}
                  y={tipTop}
                  width={tipW}
                  height={tipH}
                  rx={6}
                  fill={colors.ink}
                />
                <text
                  x={tipLeft + 12}
                  y={tipTop + 20}
                  fontSize={12}
                  fontWeight={600}
                  fill={colors.card}
                >
                  {`${hovered.stamps} ${translate("analyticsPage.loyalty.stamp_unit")}`}
                </text>
                <text x={tipLeft + 12} y={tipTop + 38} fontSize={12} fill={colors.card}>
                  {`${hovered.clients} ${translate("analyticsPage.loyalty.client_unit")}`}
                </text>
                {/* Third line only over an amber column: what that stamp pays
                    out, in the same wording rewardLabel gives the claims table
                    and the client detail page. */}
                {hoveredReward && (
                  <text
                    x={tipLeft + 12}
                    y={tipTop + 56}
                    fontSize={11}
                    fill={colors.amber}
                  >
                    {hoveredReward}
                  </text>
                )}
              </g>
            )}
          </svg>
        </>
      )}
    </div>
  );
};

export default StampDistributionChart;
